import { Navigate } from 'react-router-dom'
import { useProject } from '@/hooks/use-project'
import { useAuth } from '@/hooks/use-auth'
import { useTravelTime } from '@/hooks/use-travel-time'
import { TravelTimeChart } from '@/components/travel-time-chart'
import { buildAppHref } from '@/lib/auth'
import { Clock, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

export function TravelTimePage() {
  const auth = useAuth()
  const project = useProject()
  const { data, isLoading, error } = useTravelTime(project)

  if (!project) {
    return (
      <Navigate
        to={auth?.mode === 'admin' ? buildAppHref('/admin', { mode: 'admin', projectCode: null }) : '/'}
        replace
      />
    )
  }

  return (
    <div className="mx-auto max-w-[1200px] px-8 py-8">
      <Card className="bg-card/95 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-3xl">
            <Clock className="h-8 w-8" /> Travel Time Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="py-12 text-center text-muted-foreground text-lg">
              <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading data...
            </div>
          )}

          {error && (
            <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
              Error loading data: {(error as Error).message}
            </div>
          )}

          {data && (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">Link</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Base TT</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Base Speed</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Delay</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Current TT</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Current Speed</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">90 Min Graph</TableHead>
                  </TableRow>
                </TableHeader>
                  <TableBody>
                    {data.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-semibold">
                          <div>{item.linkName}</div>
                          {item.subtitle ? (
                            <div className="text-xs font-normal text-muted-foreground">
                              {item.subtitle}
                            </div>
                          ) : null}
                        </TableCell>
                      <TableCell>{item.baseTT}s</TableCell>
                      <TableCell>{item.baseSpeed} km/h</TableCell>
                      <TableCell className={item.delay > 0 ? 'text-destructive font-semibold' : item.delay < 0 ? 'text-success font-semibold' : ''}>
                        {item.delay > 0 ? '+' : ''}{item.delay}s
                      </TableCell>
                      <TableCell>{item.currentTT}s</TableCell>
                      <TableCell>{item.currentSpeed} km/h</TableCell>
                      <TableCell>
                        <TravelTimeChart
                          last90MinTT={item.last90MinTT}
                          last90MinBase={item.last90MinBase}
                          currentTT={item.currentTT}
                          index={index}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
